// Load LivePerson's Web Tag inside our embed, in MONITOR-ONLY mode, so every handoff carries a
// LivePerson visitor monitoring session.
//
// Why: the rep hyperlink ("CLICK HERE") button, device/OS/browser and the "Website" source only
// appear on a handoff that carries a real LP monitoring session (proven on the sandbox
// 2026-08-21). On prod that session is usually the shopper's own tirerack.com visit, not a new
// one: LivePerson keeps its ids in the LPVID and LPSID-<account> cookies, set on .tirerack.com,
// so the tag in our embed (traci.mi.tirerack.com) joins the session the site's own tag is
// logging (measured 25 Aug and 26 Oct 2026: about 93% of prod handoffs carry the shopper's site
// pages). Dev and QA never show the site journey: they run the sandbox account, not the account
// tirerack.com's tag reports into.
//
// Monitor-only: the Web Tag would otherwise render LivePerson's own chat widget inside our
// chat. We suppress its UI with CSS (lpMonitorSuppress.css) and read the session straight
// from the monitoring SDK (getVid/getSid) — monitoring is pure JS, so hiding the UI does not
// stop it. We load it only when a handoff is starting (the Connect press warms it, and
// prepareLpHandoff waits for it), so shoppers who never ask for a person get no LP session.
// TRACI-629: minting takes 1.7 to 6.4 s, so the handoff now waits up to 8 s (was 5 s, which
// lost the session on about one QA handoff in six: LivePerson then records source APP and the
// rep gets no browser).
//
// CPOC-126: about 7% of prod handoffs still got a session holding only the embed page. Before our
// tag loads, the embed snapshots the site's LPVID / LPSID-<account> cookies (lpCookieSessionForHandoff)
// and the handoff sends them as the site session; the server reads that visit first and falls back
// to the minted session when it is gone (a 404) or absent. Never logged: they are visitor ids.
//
// Cart (TRACI-629): tirerack.com pushes its cart into its own lpTag only on the cart page
// (/cart/HoldingArea.jsp). The embed reads that cart from the page context (lpCartFromPageHtml,
// or loader.js `lpCart`) and prepareLpHandoff pushes it into this session, the one the
// conversation is bound to, so the rep gets it from any page. In lower environments that is
// the sandbox account, on purpose.
//
// CSP: loading the tag requires the embed's Content-Security-Policy to allow LivePerson's
// script/connect domains (lptag.liveperson.net and the taglet/monitoring hosts). Without it
// the tag silently fails and ensureLpSession() returns null — the handoff still works, just
// without the hyperlink button.
import "./lpMonitorSuppress.css";

type LpSession = { visitorId: string; sessionId: string };
export type SiteLpSession = LpSession & { site: string };

/** LP account for this env. Mirrors infrastructure/lib/ecs-stack.ts: prod -> the prod
 *  account, everything else -> sandbox. Override with VITE_LP_MONITOR_SITE if ever needed. */
export function lpSiteForEnv(env: string | undefined, override?: string): string {
  const ovr = override?.trim();
  if (ovr) return ovr;
  return (env || "").trim() === "prod" ? "14240048" : "9486000";
}

function lpSite(): string | null {
  return lpSiteForEnv(
    import.meta.env.VITE_ENV as string | undefined,
    import.meta.env.VITE_LP_MONITOR_SITE as string | undefined,
  );
}

/** A LivePerson id as the server accepts it (lp_visit.py `_LP_ID`); anything else is dropped. */
const LP_ID = /^[A-Za-z0-9_.-]{1,80}$/;

/** The site's LivePerson session for `site`, read from its .tirerack.com cookies (LPVID and
 *  LPSID-<site>), or null when either is missing or not id-shaped. */
export function readLpCookieSession(site: string | null, cookie: string): SiteLpSession | null {
  if (!site || !/^[0-9]{1,20}$/.test(site)) return null;
  const jar = new Map<string, string>();
  for (const part of (cookie || "").split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const name = part.slice(0, eq).trim();
    if (!jar.has(name)) jar.set(name, part.slice(eq + 1).trim());
  }
  const visitorId = jar.get("LPVID") || "";
  const sessionId = jar.get(`LPSID-${site}`) || "";
  return LP_ID.test(visitorId) && LP_ID.test(sessionId) ? { site, visitorId, sessionId } : null;
}

/** undefined until taken: the cookies as they were before our own tag could rewrite them. */
let cookieSnapshot: SiteLpSession | null | undefined;

function snapshotLpCookies(site: string): void {
  if (cookieSnapshot !== undefined) return;
  try { cookieSnapshot = readLpCookieSession(site, document.cookie); } catch { cookieSnapshot = null; }
}

/** CPOC-126: the site's own LivePerson session for this handoff, from the cookies as they were
 *  before our tag loaded (or now, when it never did). Null off a tirerack.com host or without them. */
export function lpCookieSessionForHandoff(): SiteLpSession | null {
  const site = lpSite();
  if (!site || typeof document === "undefined") return null;
  snapshotLpCookies(site);
  return cookieSnapshot ?? null;
}

/** For the server's log only: whether the cookie session is missing, the one we minted, or a
 *  different one. Tells on prod whether the 7% embed-only sessions had a site cookie at all. */
export function lpCookieMatch(cookie: SiteLpSession | null, minted: LpSession | null): "none" | "same" | "different" {
  if (!cookie) return "none";
  return minted && minted.visitorId === cookie.visitorId && minted.sessionId === cookie.sessionId ? "same" : "different";
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function bootstrapTag(site: string): void {
  const w = window as any;
  if (w.lpTag && typeof w.lpTag._tagCount !== "undefined") {
    w.lpTag._tagCount += 1;
    return;
  }
  const lpTag: any = {
    site,
    section: "",
    autoStart: true,
    ovr: {},
    _v: "1.10.0",
    _tagCount: 1,
    protocol: "https:",
    events: {
      bind: (app: any, ev: any, fn: any) => lpTag.defer(() => lpTag.events.bind(app, ev, fn), 0),
      trigger: (app: any, ev: any, data: any) => lpTag.defer(() => lpTag.events.trigger(app, ev, data), 0),
    },
    defer(fn: any, when: number) {
      if (when === 0) (this._defB = this._defB || []).push(fn);
      else if (when === 1) (this._defT = this._defT || []).push(fn);
      else (this._defL = this._defL || []).push(fn);
    },
    load(src: string, chr: string, id: string) {
      setTimeout(() => this._load(src, chr, id), 0);
    },
    _load(src: string, chr: string, id: string) {
      const url = src || `${this.protocol}//${(this.ovr && this.ovr.domain) || "lptag.liveperson.net"}/tag/tag.js?site=${this.site}`;
      const s = document.createElement("script");
      s.setAttribute("charset", chr || "UTF-8");
      if (id) s.setAttribute("id", id);
      s.setAttribute("src", url);
      // TRACI-629: the tag itself blocked (CSP, ad blocker) or down: stop the handoff waiting
      // for a session. Only our own tag.js load; a taglet the tag loads later may fail alone.
      if (!src) s.addEventListener("error", () => { tagFailed = true; });
      document.getElementsByTagName("head").item(0)!.appendChild(s);
    },
    init() {
      this._timing = { start: Date.now() };
      window.addEventListener("DOMContentLoaded", () => this._domReady("contReady"), false);
      window.addEventListener("load", () => this._domReady("domReady"), false);
      if (typeof (window as any)._lptStop === "undefined") this.load();
    },
    start() {
      this.autoStart = true;
    },
    _domReady(name: string) {
      if (!this.isDom) {
        this.isDom = true;
        this.events.trigger("LPT", "DOM_READY", { t: name });
      }
      this._timing[name] = Date.now();
    },
    vars: [], dbs: [], ctn: [], sdes: [], hooks: [], identities: [], ev: [],
  };
  w.lpTag = lpTag;
  lpTag.init();
}

let loadStarted = false;
let tagFailed = false;

/** A LivePerson `cart` SDE as tirerack.com pushes it, reduced to the documented fields. */
export type LpCart = {
  type: "cart";
  total?: number;
  numItems?: number;
  currency?: string;
  products: Array<{ product: { name?: string; category?: string; sku?: string; price?: number }; quantity?: number }>;
};

const MAX_PRODUCTS = 50;
const str = (v: unknown, max: number): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim().slice(0, max) : undefined;
const num = (v: unknown): number | undefined => {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : undefined;
};

/** Keep only the documented cart fields, as plain strings and finite numbers. The cart is
 *  page-controlled data on its way to the rep's screen, so nothing else rides along. */
export function sanitizeLpCart(raw: unknown): LpCart | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const c = raw as Record<string, unknown>;
  if (c.type !== "cart") return null;
  const products = (Array.isArray(c.products) ? c.products : []).slice(0, MAX_PRODUCTS).flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const e = entry as Record<string, unknown>;
    const p = (e.product && typeof e.product === "object" ? e.product : {}) as Record<string, unknown>;
    const product = { name: str(p.name, 200), category: str(p.category, 60), sku: str(p.sku, 60), price: num(p.price) };
    if (!product.name && !product.sku) return [];
    return [{ product, quantity: num(e.quantity) }];
  });
  const cart: LpCart = { type: "cart", total: num(c.total), numItems: num(c.numItems), currency: str(c.currency, 8), products };
  if (cart.total === undefined && cart.numItems === undefined && products.length === 0) return null;
  return JSON.parse(JSON.stringify(cart)) as LpCart;   // drop the undefined keys
}

/** The tag's SDE module once it has loaded. Before that `lpTag.sdes` is the bootstrap queue,
 *  an array the tag only sends when its module starts, which can be after the conversation
 *  already exists (seen locally, 21 Oct: the cart landed 5.5 s after the escalate). */
function liveSdes(): { push: (sde: unknown) => void; send?: (sde: unknown) => void } | null {
  const sdes = (window as any).lpTag?.sdes;
  if (!sdes || Array.isArray(sdes) || typeof sdes.push !== "function" || typeof sdes.get !== "function") return null;
  return sdes;
}

/**
 * Hand LivePerson an SDE now. `lpTag.sdes.push` only queues it for the monitor's next heartbeat
 * (about 7 s on the sandbox, 22 Oct), by which time the handoff has opened the conversation and
 * the rep's view never gets the cart; on dev the handoff cart left the browser 6 s after the
 * escalate and the conversation recorded none. `send` goes out at once; `push` is the fallback.
 */
function sendSde(sdes: { push: (sde: unknown) => void; send?: (sde: unknown) => void }, sde: unknown): void {
  if (typeof sdes.send === "function") sdes.send(sde);
  else sdes.push(sde);
}

// TRACI-852: the cart outlives the page. tirerack.com only puts its cart in the cart page's
// HTML, and the embed is rebuilt on every page, so a cart seen on the cart page used to be gone
// by the time the shopper handed off from a tire page (22 Oct: the rep said "I do not see your
// cart"). The last real cart now lives in this tab's sessionStorage, per surface, with the LP
// session the last handoff was bound to.
type CartSlot = { cart: LpCart | null; at: number; handoffSid?: string };

/** A remembered cart older than this is a guess, not the cart (shopperJourney uses the same). */
export const LP_CART_MAX_AGE_MS = 2 * 60 * 60 * 1000;

function slotKey(): string {
  try {
    // The ?surface= namespace RealChatAgentService keys its storage with: a QA-extension
    // surface's (stage) cart must never ride a real chat's handoff.
    const surface = (new URLSearchParams(window.location.search).get("surface") || "").replace(/[^a-z0-9_-]/gi, "").slice(0, 40);
    return `traci.lpCart.v1${surface ? `:${surface}` : ""}`;
  } catch { return "traci.lpCart.v1"; }
}

function sessionStore(): Storage | null {
  try { return typeof window === "undefined" ? null : window.sessionStorage; } catch { return null; }
}

let slot: CartSlot | null = null;

/** The remembered state: storage when it is readable, else this page's memory only. */
function cartSlot(): CartSlot {
  if (slot) return slot;
  slot = { cart: null, at: 0 };
  try {
    const raw = sessionStore()?.getItem(slotKey());
    const v = raw ? JSON.parse(raw) : null;
    if (v && typeof v === "object") {
      slot = {
        cart: sanitizeLpCart(v.cart),
        at: Number(v.at) || 0,
        handoffSid: typeof v.handoffSid === "string" ? v.handoffSid : undefined,
      };
    }
  } catch { /* unreadable: start empty */ }
  return slot;
}

function saveSlot(): void {
  try { sessionStore()?.setItem(slotKey(), JSON.stringify(cartSlot())); } catch { /* blocked or full */ }
}

function rememberedCart(): LpCart | null {
  const s = cartSlot();
  return s.cart && Date.now() - s.at <= LP_CART_MAX_AGE_MS ? s.cart : null;
}

/** For tests: forget all in-memory state, as a new page load does; storage is kept. */
export function _resetLpMonitorForTests(): void {
  slot = null;
  resumedSid = null;
  wantLive = false;
  keptLive = false;
  keepLiveRunning = false;
  pushedCartKey = null;
  loadStarted = false;
  tagFailed = false;
  cookieSnapshot = undefined;
}

/** The LP session a resumed live chat is bound to; while set, pushes go only into it. */
let resumedSid: string | null = null;
/** Once per page and LP session: a pushed cart is not re-sent until it changes. Page memory
 *  only, so a push LP dropped on one page is retried on the next. */
let pushedCartKey: string | null = null;

/** Push the remembered cart into the live monitoring session, once per cart per session.
 *  Returns true only when it pushed just now. */
export function flushLpCart(): boolean {
  const cart = rememberedCart();
  if (!cart || typeof window === "undefined") return false;
  const mon = monitoringSDK();
  const sid = mon?.getSid?.();
  const sdes = liveSdes();
  if (!sid || !sdes) return false;
  // A resumed chat whose page minted a different LP session: the rep would never see it.
  if (resumedSid && sid !== resumedSid) return false;
  const key = `${sid}|${JSON.stringify(cart)}`;
  if (key === pushedCartKey) return false;
  try {
    sendSde(sdes, cart);
    pushedCartKey = key;
    return true;
  } catch { return false; /* the handoff proceeds without the cart */ }
}

const isEmptyCart = (c: LpCart): boolean => c.numItems === 0 || (c.products.length === 0 && !c.total);

/** True for tirerack.com's cart page (/cart, /cart/...), not /cartridge or a ?cart= query. */
export function isLpCartPage(url: unknown): boolean {
  try { return /^\/cart(\/|$)/i.test(new URL(String(url || "")).pathname); } catch { return false; }
}

/** The shopper's cart as the latest page context gave it (loader.js `lpCart`, or parsed from
 *  the page HTML). TRACI-852: a page that says nothing about the cart keeps the last one seen;
 *  an empty cart, or the cart page with no cart on it, clears it, so an emptied cart is never
 *  sent. Pushed right away when the monitoring session is live, else at handoff. */
export function setLpCart(raw: unknown, opts: { cartPage?: boolean } = {}): void {
  const cart = sanitizeLpCart(raw);
  const s = cartSlot();
  if (cart && !isEmptyCart(cart)) {
    s.cart = cart;
    s.at = Date.now();
    saveSlot();
  } else if (cart || opts.cartPage) {
    if (s.cart) { s.cart = null; s.at = Date.now(); saveSlot(); }
    pushEmptyCartIfLive();
    return;
  }
  if (!flushLpCart() && cart && !isEmptyCart(cart) && wantLive && !keptLive) void keepLpCartLive();
}

const EMPTY_CART: LpCart = { type: "cart", total: 0, numItems: 0, products: [] };

/** The shopper emptied the cart after a cart reached this page's LP session (the rep is looking
 *  at it): tell LivePerson the cart is empty now, so the rep does not keep seeing the old one. */
function pushEmptyCartIfLive(): void {
  if (!pushedCartKey || typeof window === "undefined") return;
  const sid = monitoringSDK()?.getSid?.();
  const sdes = liveSdes();
  const key = `${sid}|${JSON.stringify(EMPTY_CART)}`;
  if (!sid || !sdes || !pushedCartKey.startsWith(`${sid}|`) || pushedCartKey === key) return;
  try { sendSde(sdes, EMPTY_CART); pushedCartKey = key; } catch { /* best-effort */ }
}

const MAX_CART_SCRIPT = 20000;
const CART_SETTLE_MS = 1000;

/**
 * The cart tirerack.com gives LivePerson, read out of the page HTML the site sends with its
 * page context. The site's own chat host (clientlibs `buildAIAssistData`, which is what
 * shoppers get on tirerack.com, not our loader.js) sends `html: outerHTML` but no cart; on
 * the cart page that HTML holds an inline `lpTag.sdes.push({"type": "cart", ...})` script
 * with `//` comments after each field. Takes the last cart push; null when there is none or
 * it does not parse. The result still goes through sanitizeLpCart.
 */
export function lpCartFromPageHtml(html: unknown): unknown {
  if (typeof html !== "string" || !html) return null;
  try {
    // The last cart push that sits inside a <script> block: page text (reviews, Q&A) that
    // happens to quote one is not a cart.
    const re = /["']type["']\s*:\s*["']cart["']/g;
    let last = -1;
    let push = -1;
    for (let m = re.exec(html); m; m = re.exec(html)) {
      const at = html.lastIndexOf("lpTag.sdes.push(", m.index);
      if (at < 0 || m.index - at > 200) continue;
      if (html.lastIndexOf("<script", at) <= html.lastIndexOf("</script", at)) continue;
      last = m.index;
      push = at;
    }
    if (last < 0) return null;
    const open = html.indexOf("{", push);
    if (open < 0 || open > last) return null;
    // Copy the object literal, dropping comments outside strings, until its braces balance.
    let out = "";
    let depth = 0;
    let quote = "";
    for (let i = open; i < html.length && i - open < MAX_CART_SCRIPT; i++) {
      const c = html[i];
      if (quote) {
        out += c;
        if (c === "\\") { out += html[++i] ?? ""; continue; }
        if (c === quote) quote = "";
        continue;
      }
      if (c === '"') { quote = c; out += c; continue; }
      if (c === "/" && html[i + 1] === "/") { while (i < html.length && html[i] !== "\n") i++; out += "\n"; continue; }
      if (c === "/" && html[i + 1] === "*") { const end = html.indexOf("*/", i + 2); if (end < 0) return null; i = end + 1; continue; }
      out += c;
      if (c === "{") depth++;
      else if (c === "}" && --depth === 0) return JSON.parse(out.replace(/,(\s*[}\]])/g, "$1"));
    }
  } catch { /* not a cart we can read: hand off without it */ }
  return null;
}

/**
 * The one LivePerson step of every handoff (RealChatAgentService.escalateToHuman, which the
 * Connect button, the department picker and the agent's own "Connecting you" all reach):
 * load the monitor-only tag, wait for its visitor session, push the shopper's cart into it,
 * and only then let the conversation be created. Everything shares one budget. Null when the
 * session is not ready in time: the handoff then proceeds without ids, as before.
 */
export async function prepareLpHandoff(timeoutMs = 8000): Promise<LpSession | null> {
  const deadline = Date.now() + timeoutMs;
  resumedSid = null;   // a new conversation: whatever session forms now is the one it binds to
  const session = await ensureLpSession(timeoutMs);
  if (!session) return null;
  const s = cartSlot();
  s.handoffSid = session.sessionId;
  saveSlot();
  // The session can form before the SDE module loads; wait for it, inside the same budget.
  while (rememberedCart() && !liveSdes() && Date.now() < deadline) await new Promise((r) => setTimeout(r, 100));
  // Give a cart pushed just now a moment to reach LivePerson before the conversation exists.
  if (flushLpCart()) {
    const settle = Math.min(CART_SETTLE_MS, deadline - Date.now());
    if (settle > 0) await new Promise((r) => setTimeout(r, settle));
  }
  return session;
}

let wantLive = false;

/**
 * TRACI-852: a live chat resumed on a new page (the shopper navigated while talking to a rep).
 * When a cart is remembered, load the monitor tag and push the cart into the LP session the
 * conversation was bound to, so a cart the shopper builds during the chat reaches the rep. Only
 * that session: if this page minted a different one, nothing is pushed. No remembered cart, no
 * tag load, until a page context brings one (setLpCart calls back in).
 */
export async function keepLpCartLive(timeoutMs = 8000): Promise<boolean> {
  const s = cartSlot();
  if (!s.handoffSid) return false;
  wantLive = true;
  if (!rememberedCart() || keptLive || keepLiveRunning) return false;
  keepLiveRunning = true;
  try {
    resumedSid = s.handoffSid;
    const deadline = Date.now() + timeoutMs;
    const session = await ensureLpSession(timeoutMs);
    if (!session || session.sessionId !== s.handoffSid) return false;
    keptLive = true;   // this page's session is the conversation's: later carts push directly
    while (!liveSdes() && Date.now() < deadline) await new Promise((r) => setTimeout(r, 100));
    // The rejoined session answers getSid() from storage before this page has started with
    // LivePerson, and a cart pushed then is never sent (seen locally, 22 Oct). Start the page
    // now that the SDK is up, and give it a moment before pushing.
    try { const mon = monitoringSDK(); mon?.startPage?.(); mon?.pageLoaded?.(); } catch { /* ignore */ }
    await new Promise((r) => setTimeout(r, Math.max(0, Math.min(RESUME_SETTLE_MS, deadline - Date.now()))));
    return flushLpCart();
  } catch { return false; } finally { keepLiveRunning = false; }
}
let keepLiveRunning = false;
let keptLive = false;
const RESUME_SETTLE_MS = 2000;

function monitoringSDK(): any {
  return (window as any).lpTag?.taglets?.lp_monitoringSDK || null;
}

/**
 * Ensure a LivePerson monitoring session exists and return its ids, or null if it can't be
 * established in time (in which case the handoff simply proceeds without the hyperlink button).
 * Safe to call repeatedly — the tag loads once.
 */
export async function ensureLpSession(timeoutMs = 6000): Promise<LpSession | null> {
  const site = lpSite();
  if (!site || typeof window === "undefined") return null;
  try {
    if (!loadStarted) {
      loadStarted = true;
      snapshotLpCookies(site);   // before our tag can rewrite LPSID
      bootstrapTag(site);
    }
    const deadline = Date.now() + timeoutMs;
    let kicked = false;
    while (Date.now() < deadline && !tagFailed) {
      const mon = monitoringSDK();
      if (mon) {
        if (!kicked) {
          // The tag was injected after page load, so its own DOM_READY never fired the
          // monitoring page-start. Kick it once so the session mints.
          kicked = true;
          try { mon.startPage?.(); mon.pageLoaded?.(); } catch { /* ignore */ }
        }
        const visitorId = mon.getVid?.();
        const sessionId = mon.getSid?.();
        if (visitorId && sessionId) return { visitorId, sessionId };
      }
      await new Promise((r) => setTimeout(r, 300));
    }
  } catch { /* ignore — handoff proceeds without a session */ }
  return null;
}
